/**
 * PricingHistoryPage — Historique d'un tuple (provider, tier, unitType).
 *
 * Route : /ops/pricing/history?provider=X&tier=Y&unitType=Z
 *
 * - Sélecteurs pour changer le tuple
 * - Timeline + graphe Recharts (évolution prix)
 *
 * Accès : ROLE_PLATFORM_ADMIN.
 */

import { Alert, Button, Select } from 'antd';
import { ArrowLeft, History as HistoryIcon } from 'lucide-react';
import { useMemo, type FC } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import PricingHistoryTimeline from '@/components/pricing/PricingHistoryTimeline';
import { usePricingHistory } from './hooks/usePricingActions';
import {
  PRICING_PROVIDERS,
  PRICING_TIERS,
  PROVIDER_LABELS,
  TIER_LABELS,
  UNIT_TYPES,
  UNIT_TYPE_LABELS,
} from './types';
import type { PricingProvider, PricingTier, UnitType } from './types';

const PROVIDER_OPTIONS = PRICING_PROVIDERS.map((p) => ({
  value: p,
  label: PROVIDER_LABELS[p],
}));
const TIER_OPTIONS = PRICING_TIERS.map((t) => ({
  value: t,
  label: TIER_LABELS[t],
}));
const UNIT_OPTIONS = UNIT_TYPES.map((u) => ({
  value: u,
  label: UNIT_TYPE_LABELS[u],
}));

function asProvider(v: string | null): PricingProvider | null {
  if (!v) return null;
  return (PRICING_PROVIDERS as readonly string[]).includes(v)
    ? (v as PricingProvider)
    : null;
}
function asTier(v: string | null): PricingTier | null {
  if (!v) return null;
  return (PRICING_TIERS as readonly string[]).includes(v)
    ? (v as PricingTier)
    : null;
}
function asUnit(v: string | null): UnitType | null {
  if (!v) return null;
  return (UNIT_TYPES as readonly string[]).includes(v) ? (v as UnitType) : null;
}

const PricingHistoryPage: FC = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const provider = useMemo(
    () => asProvider(searchParams.get('provider')),
    [searchParams],
  );
  const tier = useMemo(() => asTier(searchParams.get('tier')), [searchParams]);
  const unitType = useMemo(
    () => asUnit(searchParams.get('unitType')),
    [searchParams],
  );

  const { data: entries, isLoading, error } = usePricingHistory(
    provider,
    tier,
    unitType,
  );

  const setParam = (key: 'provider' | 'tier' | 'unitType', value: string | undefined) => {
    const next = new URLSearchParams(searchParams);
    if (value) {
      next.set(key, value);
    } else {
      next.delete(key);
    }
    setSearchParams(next);
  };

  const canFetch = Boolean(provider && tier && unitType);

  return (
    <div className="space-y-6">
      <div className="fc-gradient-header rounded-2xl p-6 text-white">
        <div className="flex items-center gap-3">
          <HistoryIcon className="h-6 w-6" />
          <div>
            <h1 className="text-xl font-bold">Historique des tarifs</h1>
            <p className="mt-1 text-sm text-blue-100">
              Évolution des prix pour un tuple (provider × tier × unité)
            </p>
          </div>
        </div>
      </div>

      <div className="fc-card p-5">
        <div className="mb-4 flex items-center justify-between">
          <h2 className="text-sm font-semibold text-slate-900 dark:text-white">
            Tuple à inspecter
          </h2>
          <Button
            icon={<ArrowLeft className="h-4 w-4" />}
            onClick={() => navigate('/ops/pricing')}
          >
            Retour
          </Button>
        </div>
        <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
          <div>
            <p className="mb-1.5 text-[10px] uppercase tracking-wider text-slate-500 dark:text-slate-400">
              Provider
            </p>
            <Select<string>
              style={{ width: '100%' }}
              placeholder="Provider"
              options={PROVIDER_OPTIONS}
              value={provider ?? undefined}
              onChange={(v) => setParam('provider', v)}
              allowClear
            />
          </div>
          <div>
            <p className="mb-1.5 text-[10px] uppercase tracking-wider text-slate-500 dark:text-slate-400">
              Tier
            </p>
            <Select<string>
              style={{ width: '100%' }}
              placeholder="Tier"
              options={TIER_OPTIONS}
              value={tier ?? undefined}
              onChange={(v) => setParam('tier', v)}
              allowClear
            />
          </div>
          <div>
            <p className="mb-1.5 text-[10px] uppercase tracking-wider text-slate-500 dark:text-slate-400">
              Unité
            </p>
            <Select<string>
              style={{ width: '100%' }}
              placeholder="Unité"
              options={UNIT_OPTIONS}
              value={unitType ?? undefined}
              onChange={(v) => setParam('unitType', v)}
              allowClear
            />
          </div>
        </div>
      </div>

      {!canFetch && (
        <Alert
          type="info"
          showIcon
          message="Sélectionnez les 3 critères"
          description="Provider, tier et unité sont requis pour afficher l'historique."
        />
      )}

      {error && (
        <Alert
          type="error"
          showIcon
          message="Erreur de chargement"
          description={error.message}
        />
      )}

      {canFetch && !error && (
        <PricingHistoryTimeline entries={entries ?? []} loading={isLoading} />
      )}
    </div>
  );
};

export default PricingHistoryPage;
